import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import BlogService from "@/services/blog";
import parseHttpError from "@/utils/parseHttpError";
import {
  BlogPost,
  BlogCategory,
  BlogSearchParams,
  BlogPaginationData,
  BlogPostCard,
} from "@/types/blog";

interface UseBlogDataReturn {
  posts: BlogPostCard[];
  categories: BlogCategory[];
  pagination: BlogPaginationData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const useBlogData = (
  searchParams: BlogSearchParams = {},
): UseBlogDataReturn => {
  const [categories, setCategories] = useState<BlogCategory[]>([]);

  // Fetch categories once on mount
  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      try {
        const response = await BlogService.GetBlogCategories();
        return response.data;
      } catch (err: any) {
        console.error("Error fetching blog categories:", err);
        throw new Error(parseHttpError(err).message);
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Update categories state when data is fetched
  useEffect(() => {
    if (categoriesData) {
      setCategories(categoriesData);
    }
  }, [categoriesData]);

  // Create a stable query key based on search params
  const queryKey = useMemo(() => {
    return ["blog-posts", searchParams];
  }, [searchParams]);

  // Fetch blog posts with search/filter params
  const {
    data: postsData,
    isLoading: postsLoading,
    error: postsError,
    refetch: refetchPosts,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        const response = await BlogService.GetBlogPosts(searchParams);
        return response.data;
      } catch (err: any) {
        console.error("Error fetching blog posts:", err);
        throw new Error(parseHttpError(err).message);
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });

  // Transform full blog posts to card format
  const transformedPosts: BlogPostCard[] = useMemo(() => {
    if (!postsData?.posts) return [];

    return postsData.posts.map((post: BlogPost) => ({
      id: post.id,
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      author: {
        name: post.author.name,
        avatar: post.author.avatar,
      },
      category: {
        name: post.category.name,
        slug: post.category.slug,
      },
      featuredImage: {
        url: post.featuredImage.url,
        alt: post.featuredImage.alt,
      },
      publishedAt: post.publishedAt,
      readingTime: post.readingTime,
      views: post.views,
    }));
  }, [postsData]);

  // Combine loading states
  const isLoading = categoriesLoading || postsLoading;

  // Determine error state
  const error = useMemo(() => {
    if (categoriesError) {
      return categoriesError instanceof Error
        ? categoriesError.message
        : "Failed to load categories";
    }
    if (postsError) {
      return postsError instanceof Error
        ? postsError.message
        : "Failed to load blog posts";
    }
    return null;
  }, [categoriesError, postsError]);

  // Extract pagination data
  const pagination: BlogPaginationData | null = postsData?.pagination || null;

  return {
    posts: transformedPosts,
    categories,
    pagination,
    isLoading,
    error,
    refetch: refetchPosts,
  };
};

export default useBlogData;
